/**
 * Attendance & Engagement API — Meeting Tools analytics.
 */

import { livekitAuthService as authService } from "../utils/authService";

export type AttendanceEngagementStudent = {
  student_id: string;
  name: string;
  email: string;
  usn: string;
  phone: string;
  batch: string;
  present: boolean;
  /** True when Redis last_seen is within the active threshold (180s). */
  active: boolean;
  attendance_pct: number;
  attended_minutes: number;
  session_duration_minutes: number;
  engagement_pct: number;
  distraction: number | null;
  /** Minutes tab-hidden or window blurred while present (null = no telemetry yet). */
  away_minutes: number | null;
  swup: "Understood" | "Partially Understood" | "Not Understood" | null;
  engaged: boolean;
};

export type AttendanceEngagementSummary = {
  total_students: number;
  attendees: number;
  attendees_pct: number;
  absentees: number;
  absentees_pct: number;
  engaged: number;
  engaged_pct: number;
  active_now: number;
  active_now_pct: number;
};

export type AttendanceEngagementReport = {
  success: boolean;
  session_id: string;
  session_duration_minutes: number;
  summary: AttendanceEngagementSummary;
  students: AttendanceEngagementStudent[];
};

function asStudent(raw: Record<string, unknown>): AttendanceEngagementStudent {
  const swupRaw = raw.swup == null ? null : String(raw.swup);
  const swup =
    swupRaw === "Understood" ||
    swupRaw === "Partially Understood" ||
    swupRaw === "Not Understood"
      ? (swupRaw as AttendanceEngagementStudent["swup"])
      : null;
  const distraction =
    raw.distraction == null || raw.distraction === ""
      ? null
      : Number(raw.distraction);
  const away =
    raw.away_minutes == null || raw.away_minutes === ""
      ? null
      : Number(raw.away_minutes);
  return {
    student_id: String(raw.student_id ?? ""),
    name: String(raw.name ?? ""),
    email: String(raw.email ?? ""),
    usn: String(raw.usn ?? ""),
    phone: String(raw.phone ?? ""),
    batch: String(raw.batch ?? ""),
    present: Boolean(raw.present),
    active: Boolean(raw.active),
    attendance_pct: Number(raw.attendance_pct ?? 0),
    attended_minutes: Number(raw.attended_minutes ?? 0),
    session_duration_minutes: Number(raw.session_duration_minutes ?? 0),
    engagement_pct: Number(raw.engagement_pct ?? 0),
    distraction: Number.isFinite(distraction as number) ? distraction : null,
    away_minutes: Number.isFinite(away as number) ? away : null,
    swup,
    engaged: Boolean(raw.engaged),
  };
}

export async function fetchAttendanceEngagement(
  sessionId: string,
): Promise<AttendanceEngagementReport> {
  const res = await authService.get(
    `/session/sessions/${sessionId}/attendance-engagement/`,
  );
  const data = res?.data || {};
  const summaryRaw = (data.summary || {}) as Record<string, unknown>;
  const studentsRaw = Array.isArray(data.students) ? data.students : [];
  return {
    success: Boolean(data.success),
    session_id: String(data.session_id ?? sessionId),
    session_duration_minutes: Number(data.session_duration_minutes ?? 0),
    summary: {
      total_students: Number(summaryRaw.total_students ?? 0),
      attendees: Number(summaryRaw.attendees ?? 0),
      attendees_pct: Number(summaryRaw.attendees_pct ?? 0),
      absentees: Number(summaryRaw.absentees ?? 0),
      absentees_pct: Number(summaryRaw.absentees_pct ?? 0),
      engaged: Number(summaryRaw.engaged ?? 0),
      engaged_pct: Number(summaryRaw.engaged_pct ?? 0),
      active_now: Number(summaryRaw.active_now ?? 0),
      active_now_pct: Number(summaryRaw.active_now_pct ?? 0),
    },
    students: studentsRaw.map((row: Record<string, unknown>) => asStudent(row)),
  };
}
